import type {
	AffectationService,
	IdeeRefusee,
	ImportIdeesResultat,
} from '../../interfaces/simulation/ideeImportable';

// Toast annonce a l'issue d'un import d'idees : danger pour une idee perdue
// lors d'un import concurrent, information pour une affectation de service
export interface ToastImport {
	danger?: boolean;
	texte: string;
	titre: string;
}

const RAISONS: Record<'defaut' | 'premier', string> = {
	defaut: 'votre service par défaut',
	premier: 'le premier de vos services',
};

const getToastGroupe = (affectations: AffectationService[]): ToastImport => {
	const nombre = affectations.length;
	const [{ libelleIdee, libelleService, source }] = affectations;
	if (source === 'aucun' || !libelleService) {
		return {
			texte: `Aucun service accessible ne peut être affecté. Renseignez le service sur ${nombre > 1 ? 'les demandes concernées' : 'la demande'}.`,
			titre:
				nombre > 1 ? `${nombre} demandes sans service` : 'Demande sans service',
		};
	}
	const quoi =
		nombre > 1
			? `${nombre} idées sans service ont été affectées`
			: `« ${libelleIdee} », sans service, a été affectée`;
	return {
		texte: `${quoi} au service ${libelleService}, ${RAISONS[source]}. Vous pouvez le modifier sur ${nombre > 1 ? 'chaque demande' : 'la demande'}.`,
		titre: `Service ${libelleService} affecté`,
	};
};

// Un toast par service affecte. En pratique, toutes les idees sans service d'un
// meme import recoivent le meme service (meme utilisateur, meme ligne). Une
// idee avec service n'apparait pas dans les affectations : aucun toast
export const getToastsAffectation = (
	affectations: AffectationService[],
): ToastImport[] => {
	const groupes = new Map<string, AffectationService[]>();
	for (const affectation of affectations) {
		const cle = `${affectation.source}|${affectation.libelleService ?? ''}`;
		const groupe = groupes.get(cle);
		if (groupe) groupe.push(affectation);
		else groupes.set(cle, [affectation]);
	}
	return [...groupes.values()].map(getToastGroupe);
};

// Un toast par idee reservee entre-temps par un autre import (le premier qui
// importe gagne). L'utilisateur ou la ligne de la demande deja liee peuvent
// etre inconnus (demande supprimee entre-temps) : on omet alors la mention
export const getToastsRefus = (refusees: IdeeRefusee[]): ToastImport[] =>
	refusees.map(({ libelle, ligne, numero, utilisateur }) => ({
		danger: true,
		texte: `Idée ID-${numero} « ${libelle} » vient d'être importée en parallèle${utilisateur ? ` par ${utilisateur}` : ''}${ligne ? ` sur la ligne ${ligne}` : ''}.`,
		titre: 'Import impossible',
	}));

// Toasts d'un import, empiles : les idees perdues d'abord, puis les services
// affectes
export const getToastsImport = ({
	affectations,
	refusees,
}: Pick<ImportIdeesResultat, 'affectations' | 'refusees'>) => [
	...getToastsRefus(refusees),
	...getToastsAffectation(affectations),
];
